import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * FIN-0110: Theme Service - User Toggle Dark Mode
 *
 * Manages theme preference with the following behavior:
 * 1. On init: Check localStorage for stored preference
 * 2. If no preference: Use system preference (prefers-color-scheme)
 * 3. User can explicitly set: 'light' | 'dark' | 'system'
 * 4. Changes are persisted to localStorage and applied to <html> element
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private preferenceSubject = new BehaviorSubject<ThemePreference>(this.loadPreference());
  private effectiveThemeSubject = new BehaviorSubject<EffectiveTheme>(this.computeEffectiveTheme());
  private mediaQuery: MediaQueryList | null = null;

  /** Observable of user's explicit preference (light/dark/system) */
  preference$: Observable<ThemePreference> = this.preferenceSubject.asObservable();

  /** Observable of the actual applied theme (light/dark only) */
  effectiveTheme$: Observable<EffectiveTheme> = this.effectiveThemeSubject.asObservable();

  constructor() {
    // Apply theme immediately
    this.applyTheme();

    // Listen for system preference changes (only matters when preference is 'system')
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.mediaQuery.addEventListener('change', () => {
        if (this.preferenceSubject.value === 'system') {
          this.updateEffectiveTheme();
        }
      });
    }
  }

  /** Get current preference */
  get preference(): ThemePreference {
    return this.preferenceSubject.value;
  }

  /** Get current effective theme */
  get effectiveTheme(): EffectiveTheme {
    return this.effectiveThemeSubject.value;
  }

  /** Check if currently in dark mode */
  get isDark(): boolean {
    return this.effectiveTheme === 'dark';
  }

  /**
   * Set theme preference
   * @param preference - 'light' | 'dark' | 'system'
   */
  setPreference(preference: ThemePreference): void {
    console.log('[ThemeService] Setting preference:', preference);

    // Store in localStorage
    if (typeof localStorage !== 'undefined') {
      if (preference === 'system') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, preference);
      }
    }

    this.preferenceSubject.next(preference);
    this.updateEffectiveTheme();
  }

  /** Toggle between light and dark (sets explicit preference, not 'system') */
  toggle(): void {
    const newPreference: ThemePreference = this.effectiveTheme === 'dark' ? 'light' : 'dark';
    this.setPreference(newPreference);
  }

  /** Cycle through: system -> light -> dark -> system */
  cycle(): void {
    const current = this.preferenceSubject.value;
    let next: ThemePreference;

    switch (current) {
      case 'system':
        next = 'light';
        break;
      case 'light':
        next = 'dark';
        break;
      case 'dark':
        next = 'system';
        break;
      default:
        next = 'system';
    }

    this.setPreference(next);
  }

  /** Load preference from localStorage */
  private loadPreference(): ThemePreference {
    if (typeof localStorage === 'undefined') {
      return 'system';
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return 'system';
  }

  /** Compute effective theme based on preference and system */
  private computeEffectiveTheme(): EffectiveTheme {
    const preference = this.preferenceSubject?.value ?? this.loadPreference();

    if (preference === 'light') return 'light';
    if (preference === 'dark') return 'dark';

    // System preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    // Default to light if can't determine
    return 'light';
  }

  /** Update effective theme and apply to DOM */
  private updateEffectiveTheme(): void {
    const effective = this.computeEffectiveTheme();
    this.effectiveThemeSubject.next(effective);
    this.applyTheme();
  }

  /** Apply theme class to document */
  private applyTheme(): void {
    if (typeof document === 'undefined') return;

    const effective = this.computeEffectiveTheme();
    const html = document.documentElement;

    if (effective === 'dark') {
      html.classList.add('dark');
      html.classList.remove('light');
    } else {
      html.classList.remove('dark');
      html.classList.add('light');
    }

    // Also set color-scheme for native elements (scrollbars, form controls)
    html.style.colorScheme = effective;

    console.log('[ThemeService] Applied theme:', effective, '(preference:', this.preferenceSubject.value, ')');
  }
}
